import { Capacitor, registerPlugin } from '@capacitor/core'

export const ANALYTICS_EVENTS = Object.freeze({
  SCREEN_VIEW: 'screen_view',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  TIMETABLE_VIEWED: 'timetable_viewed',
  NEXT_CLASS_VIEWED: 'next_class_viewed',
  CLASSROOM_NAVIGATION_STARTED: 'classroom_navigation_started',
  CAFETERIA_MENU_VIEWED: 'cafeteria_menu_viewed',
  NOTICE_DETAIL_VIEWED: 'notice_detail_viewed',
  SUGI_ANSWER_VIEWED: 'sugi_answer_viewed',
  TIMETABLE_SAVED: 'timetable_saved',
  PUSH_PERMISSION_REQUESTED: 'push_permission_requested',
  PUSH_PERMISSION_RESULT: 'push_permission_result',
  PUSH_RECEIVED: 'push_received',
  NOTIFICATION_CANDIDATE_CREATED: 'notification_candidate_created',
  NOTIFICATION_SUPPRESSED: 'notification_suppressed',
  NOTIFICATION_SENT: 'notification_sent',
  NOTIFICATION_OPENED: 'notification_opened',
  NOTIFICATION_TARGET_OPENED: 'notification_target_opened',
  NOTIFICATION_PERMISSION_PROMPTED: 'notification_permission_prompted',
  NOTIFICATION_PERMISSION_GRANTED: 'notification_permission_granted',
  NOTIFICATION_PERMISSION_DENIED: 'notification_permission_denied',
  NOTIFICATION_OPT_OUT: 'notification_opt_out',
  SEARCH_SUBMITTED: 'search_submitted',
  SEARCH_RESULT_SELECTED: 'search_result_selected',
  SUGI_QUERY_SUBMIT: 'sugi_query_submit',
  FEEDBACK_REPORT_SUBMIT: 'feedback_report_submit',
  NOTICE_IMPRESSION: 'notice_impression',
  NOTICE_OPEN: 'notice_open',
  TIMETABLE_CREATE_START: 'timetable_create_start',
  COURSE_SEARCH: 'course_search',
  COURSE_SELECT: 'course_select',
  COURSE_ADD: 'course_add',
  COURSE_REMOVE: 'course_remove',
  TIMETABLE_SAVE: 'timetable_save',
  RECOMMENDATION_START: 'recommendation_start',
  RECOMMENDATION_GENERATE: 'recommendation_generate',
  RECOMMENDATION_SUCCESS: 'recommendation_success',
  RECOMMENDATION_PLAN_VIEW: 'recommendation_plan_view',
  RECOMMENDATION_APPLY: 'recommendation_apply',
  RECOMMENDATION_FAIL: 'recommendation_fail',
  ALTERNATIVE_PLAN_VIEW: 'alternative_plan_view',
  ALTERNATIVE_PLAN_SAVE: 'alternative_plan_save',
  REGISTRATION_HUB_VIEW: 'registration_hub_view',
  APPLICATION_ORDER_SAVE: 'application_order_save',
  REGISTRATION_READY_COMPLETE: 'registration_ready_complete',
  SHUTTLE_ROUTE_VIEW: 'shuttle_route_view',
  CAFETERIA_MENU_VIEW: 'cafeteria_menu_view',
  CAMPUS_PLACE_OPEN: 'campus_place_open',
  SIGN_UP_COMPLETE: 'sign_up_complete',
  LOGIN_COMPLETE: 'login_complete',
  LOGOUT: 'logout',
  PUSH_PERMISSION_STATUS: 'push_permission_status',
  PUSH_TOKEN_REGISTERED: 'push_token_registered',
  PUSH_OPENED: 'push_opened',
  PUSH_DESTINATION_VIEWED: 'push_destination_viewed',
})

const strings = values => ({ type: 'string', values })
const number = { type: 'number' }
const notificationTypes = ['NONE', 'FIRST_CLASS', 'NEXT_CLASS', 'CLASS_CHANGE', 'CLASS_CANCEL', 'PERSONAL_EVENT', 'TOMORROW_PREVIEW', 'SHUTTLE', 'CAFETERIA', 'TIMETABLE_ACTIVATION', 'FEATURE_DISCOVERY', 'remote', 'unknown']

// The sole product-event contract. Parameters absent from this object never
// leave the app. Enum lists deliberately contain only low-cardinality values.
export const EVENT_CONTRACT = Object.freeze({
  screen_view: { category: 'Exposure', required: { screen_name: strings() }, optional: { source: strings(['push', 'tab', 'deep_link']) } },
  // Retention v1. These are emitted only after the corresponding content or
  // navigation state is usable; a route render or a button tap alone is not a
  // value event.
  onboarding_completed: { category: 'Success', required: {}, optional: {} },
  timetable_viewed: { category: 'Value', required: { source: strings(['tab', 'home', 'push', 'deep_link', 'return']) }, optional: { has_classes_today: { type: 'boolean' } } },
  next_class_viewed: { category: 'Value', required: { source: strings(['home', 'timetable', 'push', 'deep_link']) }, optional: { time_to_class_bucket: strings(['now', 'under_30m', '30m_2h', '2h_plus', 'next_day']), has_location: { type: 'boolean' } } },
  classroom_navigation_started: { category: 'Value', required: { source: strings(['home', 'timetable', 'map', 'sugi']), destination_type: strings(['classroom']) }, optional: {} },
  cafeteria_menu_viewed: { category: 'Value', required: { source: strings(['home', 'tab', 'push', 'sugi']), meal_period: strings(['breakfast', 'lunch', 'dinner', 'unknown']), date_offset: number }, optional: {} },
  notice_detail_viewed: { category: 'Value', required: { source: strings(['list', 'home', 'push', 'sugi', 'in_app']), notice_category: strings(['academic', 'facility', 'scholarship', 'it', 'all', 'other']) }, optional: {} },
  sugi_answer_viewed: { category: 'Value', required: { source: strings(['manual', 'popular', 'recent', 'recommended', 'chip']), answer_type: strings(['answer', 'cards', 'fallback']), response_time_bucket: strings(['under_1s', '1s_3s', '3s_plus']) }, optional: {} },
  timetable_saved: { category: 'Value', required: { source: strings(['manual', 'recommendation', 'import', 'restore']), course_count: number }, optional: {} },
  push_permission_requested: { category: 'System', required: { source: strings(['app_start', 'cafeteria', 'settings', 'support', 'class_reminder']) }, optional: {} },
  push_permission_result: { category: 'System', required: { permission_status: strings(['granted', 'denied', 'provisional', 'unknown']), source: strings(['app_start', 'cafeteria', 'settings', 'support', 'class_reminder']) }, optional: {} },
  push_received: { category: 'System', required: { app_state: strings(['foreground', 'background', 'unknown']) }, optional: { campaign_id: strings() } },
  notification_candidate_created: { category: 'System', required: { candidate_type: strings(notificationTypes), channel: strings(['local', 'remote_push', 'retention_dry_run']) }, optional: { feature: strings(['MAP', 'PERSONAL_EVENT', 'NOTICE', 'CAFETERIA', 'map', 'personalEvent', 'notice', 'cafeteria']) } },
  notification_suppressed: { category: 'System', required: { candidate_type: strings(notificationTypes), suppression_code: strings(['DAILY_SOFT_CAP', 'WEEKLY_DISCOVERY_CAP', 'UTILITY_ALREADY_SENT', 'FEATURE_ALREADY_USED', 'FEATURE_RECENTLY_SUGGESTED', 'NO_CAMPUS_REASON', 'DORMANT_FREQUENCY_HOLD', 'PREFERENCE_DISABLED', 'OS_PERMISSION_UNAVAILABLE', 'STALE_TIMETABLE', 'NO_VALID_CANDIDATE', 'NO_VALID_DISCOVERY_FEATURE', 'HAS_TIMETABLE', 'ACTIVATION_FREQUENCY_HOLD', 'NO_PUSH_TOKEN', 'OUTSIDE_DELIVERY_WINDOW']) }, optional: { feature: strings(['MAP', 'PERSONAL_EVENT', 'NOTICE', 'CAFETERIA', 'map', 'personalEvent', 'notice', 'cafeteria']) } },
  notification_sent: { category: 'System', required: { notification_type: strings(notificationTypes), channel: strings(['local', 'remote_push']) }, optional: { feature: strings(['MAP', 'PERSONAL_EVENT', 'NOTICE', 'CAFETERIA', 'map', 'personalEvent', 'notice', 'cafeteria']) } },
  notification_opened: { category: 'Action', required: { notification_type: strings(notificationTypes), channel: strings(['local', 'remote_push']) }, optional: { feature: strings(['MAP', 'PERSONAL_EVENT', 'NOTICE', 'CAFETERIA', 'map', 'personalEvent', 'notice', 'cafeteria']) } },
  notification_target_opened: { category: 'Success', required: { destination: strings(['home', 'notice', 'timetable', 'registration', 'shuttle', 'cafeteria', 'map', 'support', 'notifications']), notification_type: strings(notificationTypes) }, optional: { feature: strings(['MAP', 'PERSONAL_EVENT', 'NOTICE', 'CAFETERIA', 'map', 'personalEvent', 'notice', 'cafeteria']) } },
  notification_permission_prompted: { category: 'System', required: { source: strings(['app_start', 'cafeteria', 'settings', 'support', 'class_reminder']) }, optional: {} },
  notification_permission_granted: { category: 'Success', required: { source: strings(['app_start', 'cafeteria', 'settings', 'support', 'class_reminder']) }, optional: {} },
  notification_permission_denied: { category: 'System', required: { source: strings(['app_start', 'cafeteria', 'settings', 'support', 'class_reminder']) }, optional: {} },
  notification_opt_out: { category: 'Action', required: { notification_type: strings(['all', 'class', 'FIRST_CLASS', 'NEXT_CLASS', 'TOMORROW_PREVIEW', 'personal_event', 'remote', 'TIMETABLE_ACTIVATION', 'FEATURE_DISCOVERY', 'recommendations']) }, optional: {} },
  search_submitted: { category: 'Action', required: { search_scope: strings(['course']), source: strings(['manual_timetable', 'required_courses']) }, optional: { filter_count: number, has_results: { type: 'boolean' } } },
  search_result_selected: { category: 'Action', required: { search_scope: strings(['course']), source: strings(['manual_timetable', 'required_courses']) }, optional: { result_type: strings(['catalog', 'required']) } },
  sugi_query_submit: { category: 'Value', required: { source: strings(['manual', 'popular', 'recent', 'recommended', 'chip']) }, optional: {} },
  feedback_report_submit: { category: 'Value', required: {}, optional: {} },
  notice_impression: { category: 'Exposure', required: { source: strings(['in_app']) }, optional: { category: strings(['academic', 'facility', 'scholarship', 'it', 'all']) } },
  notice_open: { category: 'Value', required: { category: strings(['academic', 'facility', 'scholarship', 'it', 'all']), source: strings(['list', 'home', 'push', 'sugi', 'in_app']) }, optional: {} },
  timetable_create_start: { category: 'Action', required: { source: strings(['manual', 'recommendation']) }, optional: {} },
  course_search: { category: 'Action', required: { source: strings(['manual_timetable', 'required_courses']) }, optional: { filter_count: number, has_results: { type: 'boolean' } } },
  course_select: { category: 'Action', required: { source: strings(['manual_timetable', 'required_courses', 'timetable']) }, optional: { course_type: strings(['catalog', 'required', 'manual']) } },
  course_add: { category: 'Action', required: { source: strings(['timetable', 'manual_timetable', 'required_courses', 'recommendation']) }, optional: {} },
  course_remove: { category: 'Action', required: { source: strings(['timetable', 'manual_timetable', 'required_courses', 'recommendation']) }, optional: {} },
  timetable_save: { category: 'Value', required: { source: strings(['manual', 'recommendation']), course_count: number }, optional: { total_credits: number } },
  recommendation_start: { category: 'Action', required: {}, optional: { source: strings(['timetable']) } },
  recommendation_generate: { category: 'Action', required: { target_credits: number, preference_count: number }, optional: {} },
  recommendation_success: { category: 'Success', required: { result_count: number }, optional: { duration_ms: number } },
  recommendation_plan_view: { category: 'Exposure', required: { plan_variant: strings(['A', 'B', 'C']) }, optional: {} },
  recommendation_apply: { category: 'Value', required: { plan_variant: strings(['A', 'B', 'C']), course_count: number }, optional: { total_credits: number } },
  recommendation_fail: { category: 'System', required: { failure_reason: strings(['no_result', 'invalid_input', 'timeout', 'unknown']) }, optional: {} },
  alternative_plan_view: { category: 'Exposure', required: { plan_variant: strings(['B', 'C']) }, optional: {} },
  alternative_plan_save: { category: 'Value', required: { plan_variant: strings(['B', 'C']), course_count: number }, optional: {} },
  registration_hub_view: { category: 'Exposure', required: {}, optional: {} },
  application_order_save: { category: 'Value', required: { course_count: number }, optional: {} },
  registration_ready_complete: { category: 'Value', required: {}, optional: {} },
  shuttle_route_view: { category: 'Value', required: { route_type: strings(['to_school', 'from_school', 'campus']) }, optional: {} },
  cafeteria_menu_view: { category: 'Value', required: { restaurant_type: strings(['lecture', 'staff']) }, optional: { day_offset: number } },
  campus_place_open: { category: 'Value', required: { place_category: strings(['building', 'facility', 'shuttle_stop', 'route']) }, optional: {} },
  sign_up_complete: { category: 'Success', required: { provider: strings(['kakao', 'apple', 'google']) }, optional: {} },
  login_complete: { category: 'Action', required: { provider: strings(['kakao', 'apple', 'google']) }, optional: {} },
  logout: { category: 'Action', required: { provider: strings(['kakao', 'apple', 'google', 'unknown']) }, optional: {} },
  // Legacy events remain accepted through the transition so existing reports
  // continue to receive data. New code must use push_permission_result.
  push_permission_status: { category: 'System', required: { status: strings(['granted', 'denied', 'prompt', 'unknown']), platform: strings(['ios', 'android', 'web']) }, optional: {} },
  push_token_registered: { category: 'System', required: { platform: strings(['ios', 'android', 'web']), result: strings(['success', 'failure']) }, optional: {} },
  push_opened: { category: 'Action', required: { source: strings(['push']) }, optional: { campaign_id: strings() } },
  push_destination_viewed: { category: 'Success', required: { destination: strings(['home', 'notice', 'timetable', 'registration', 'shuttle', 'cafeteria', 'map', 'support']), source: strings(['push']) }, optional: { campaign_id: strings() } },
})

const FEATURE_EVENTS = new Set(Object.keys(EVENT_CONTRACT).filter(eventName => eventName !== ANALYTICS_EVENTS.SCREEN_VIEW))
export const EVENT_PARAM_SCHEMA = Object.freeze(Object.fromEntries(Object.entries(EVENT_CONTRACT).map(([name, definition]) => [name, [...Object.keys(definition.required), ...Object.keys(definition.optional)]])))
export const USER_PROPERTY_CONTRACT = Object.freeze({
  grade_band: strings(['1', '2', '3', '4', 'unknown']),
  auth_state: strings(['anonymous', 'logged_in']),
  has_timetable: strings(['yes', 'no']),
  push_enabled: strings(['yes', 'no', 'unknown']),
  onboarding_state: strings(['completed', 'incomplete']),
})

const NativeAnalytics = registerPlugin('NativeAnalytics')

let lastScreen = null

// Capacitor WKWebView(iOS)에서는 gtag.js가 로드되고 dataLayer에 이벤트가 쌓여도 실제
// google-analytics.com/g/collect 요청이 발생하지 않는다(Safari Web Inspector로 확인 —
// 직접 fetch한 g/collect는 성공하므로 네트워크 차단이 아니라 WKWebView 안 gtag 전송 경로
// 자체의 한계). 그래서 네이티브 플랫폼에서는 gtag를 쓰지 않고 NativeAnalyticsPlugin(iOS)/
// Firebase Analytics(Android)로 직접 보낸다. 웹은 기존 gtag 그대로 유지.
function defaults() {
  return {
    send: typeof window === 'undefined' ? null : window.gtag,
    mode: import.meta.env.MODE,
    isNative: Capacitor.isNativePlatform(),
    native: NativeAnalytics,
  }
}

function validValue(value, rule) {
  if (rule.type === 'number') return typeof value === 'number' && Number.isFinite(value)
  if (rule.type === 'boolean') return typeof value === 'boolean'
  return typeof value === 'string' && value.length > 0 && (!rule.values || rule.values.includes(value))
}

function safeParams(eventName, params, options) {
  const definition = EVENT_CONTRACT[eventName]
  if (!definition || !params || typeof params !== 'object' || Array.isArray(params)) {
    if (definition && Object.keys(definition.required).length && options?.mode === 'development') console.warn('[analytics] missing event payload', eventName)
    return null
  }
  const rules = { ...definition.required, ...definition.optional }
  const sanitized = {}
  let valid = true
  for (const [name, rule] of Object.entries(definition.required)) {
    if (!validValue(params[name], rule)) { valid = false; if (options?.mode === 'development') console.warn('[analytics] invalid required parameter', eventName, name); continue }
    sanitized[name] = params[name]
  }
  for (const [name, rule] of Object.entries(definition.optional)) {
    if (!Object.prototype.hasOwnProperty.call(params, name)) continue
    if (!validValue(params[name], rule)) { if (options?.mode === 'development') console.warn('[analytics] invalid optional parameter', eventName, name); continue }
    sanitized[name] = params[name]
  }
  if (!valid) return null
  for (const name of Object.keys(params)) {
    if (!rules[name] && options?.mode === 'development') console.warn('[analytics] undeclared parameter removed', eventName, name)
  }
  return sanitized
}

function reportError(error, options) {
  if (options?.mode === 'development') console.warn('[analytics]', error)
}

function emitAcceptedEvent(eventName) {
  try { window.dispatchEvent(new CustomEvent('suguide-analytics-event-accepted', { detail: { eventName } })) } catch { /* SSR/non-DOM */ }
}

export function trackScreenView(screenName, sourceOrOptions, maybeOptions) {
  const source = typeof sourceOrOptions === 'string' ? sourceOrOptions : null
  const options = (typeof sourceOrOptions === 'object' && sourceOrOptions) || maybeOptions || defaults()
  if (options.mode !== 'production' || !screenName || screenName === lastScreen) return false
  lastScreen = screenName
  if (options.isNative) {
    try { options.native?.trackScreen?.({ screenName, ...(source ? { source } : {}) })?.catch?.(() => {}); return true } catch { return false }
  }
  if (typeof options.send !== 'function') return false
  try {
    options.send('event', ANALYTICS_EVENTS.SCREEN_VIEW, { firebase_screen: screenName, screen_name: screenName, ...(source ? { source } : {}) })
    return true
  } catch (error) { reportError(error, options); return false }
}

export function trackEvent(eventName, params = {}, options = defaults()) {
  if (options.mode !== 'production' || !FEATURE_EVENTS.has(eventName)) return false
  const sanitizedParams = safeParams(eventName, params, options)
  if (sanitizedParams == null) return false
  if (options.isNative) {
    try {
      const payload = Object.keys(sanitizedParams).length ? { eventName, params: sanitizedParams } : { eventName }
      options.native?.trackEvent?.(payload)?.catch?.(() => {})
      emitAcceptedEvent(eventName)
      return true
    } catch (error) { reportError(error, options); return false }
  }
  if (typeof options.send !== 'function') return false
  try {
    if (Object.keys(sanitizedParams).length) options.send('event', eventName, sanitizedParams)
    else options.send('event', eventName)
    emitAcceptedEvent(eventName)
    return true
  } catch (error) { reportError(error, options); return false }
}

// Kept for zero-parameter v2 events only. New product events should use trackEvent.
export function trackFeatureEvent(eventName, options = defaults()) {
  return trackEvent(eventName, {}, options)
}

export function setAnalyticsUserId(userId, options = defaults()) {
  if (options.mode !== 'production') return false
  const value = typeof userId === 'string' && userId.length > 0 ? userId : null
  if (options.isNative) {
    try { options.native?.setUserId?.({ userId: value })?.catch?.(() => {}); return true } catch { return false }
  }
  if (typeof options.send !== 'function') return false
  try { options.send('set', { user_id: value }); return true } catch { return false }
}

export function setUserProperty(name, value, options = defaults()) {
  const rule = USER_PROPERTY_CONTRACT[name]
  if (options.mode !== 'production' || !rule || !validValue(value, rule)) return false
  if (options.isNative) {
    try { options.native?.setUserProperty?.({ name, value })?.catch?.(() => {}); return true } catch { return false }
  }
  if (typeof options.send !== 'function') return false
  try {
    options.send('set', 'user_properties', { [name]: value })
    return true
  } catch { return false }
}

export function setAnalyticsEnabled(enabled, options = defaults()) {
  if (options.isNative) {
    try { options.native?.setEnabled?.({ enabled })?.catch?.(() => {}); return true } catch { return false }
  }
  return false
}

export function resetAnalyticsStateForTests() { lastScreen = null }
